import React, {useContext} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {SelectFieldProps} from '@src/@types/components/Form';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import CustomSelect, {prepareRawOptions} from '@src/components/Fields/CustomSelect';
import TriggerableField from '../utils/TriggerableField';
import {LocaleContext} from '@src/contexts/LocaleContext';
import classnames from '@src/helpers/classnames';

export default function SelectField<Model extends IEmptyModel>(props: SelectFieldProps<Model>) {
    const {locale} = useContext(LocaleContext);
    const [design] = useContext(FormFieldDesignContext);
    const {control} = useFormContext<Model>();

    let options = prepareRawOptions(props.config.options ?? [], locale);

    const fieldName = props.fieldName as any;
    const modelOptions = props.modelOptions ?? {};
    if (typeof fieldName === 'string' && fieldName in modelOptions) {
        options = [...options, ...prepareRawOptions(modelOptions[fieldName] ?? [])];
    }

    const {getFieldState, formState} = useFormContext<Model>();
    const isInvalid = (props as any).isInvalid ?? Boolean(getFieldState(props.fieldName as any, formState).error);

    return <Controller
            name={fieldName}
            control={control}
            render={({fieldState: {error}, field: {value, onChange, onBlur}}) => {
                return <TriggerableField
                        trigger={props.config.trigger}
                        componentToRender={(CustomSelect<unknown>)}
                        name={String(props.fieldName)}
                        label={String(props.fieldLabel)}
                        formName={props.formName}
                        options={options}
                        size={props.config.size}
                        emptyOption={props.config.emptyOption}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className={classnames({
                            'is-invalid': isInvalid,
                        }, props.config.cssClass, props.className)}
                        attributes={props.config.attributes}
                        isInvalid={Boolean(error)}
                        multiple={props.config.multiple}
                        clearable={props.config.clearable}
                        searchable={props.config.readOnly === true ? false : undefined}
                        disabled={(
                                props.config.readOnly === true ||
                                props.config.disabled === true ||
                                design.fieldsAreDisabled ||
                                design.fieldsAreReadOnly ||
                                props.forceDisabled
                        )}
                        readOnly={props.config.readOnly === true || design.fieldsAreReadOnly || props.forceReadonly}
                        customStyles={props.customStyles}
                        customTheme={props.customTheme}
                />;
            }}
    />;
}
